import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { NoTrailingSegment, SuggestionString } from '@utils/typescript'
import {
  additionalClientInstance,
  clientInstance,
  localClientInstance,
} from './axios-instance'

const clientInstanceMap = {
  clientInstance,
  additionalClientInstance,
  localClientInstance,
}

type ParamConfig = Omit<AxiosRequestConfig, 'url' | 'method' | 'data'>

type ClientOptions = {
  type: SuggestionString<'api'>
  version: `v${number}`
  noVersioning: true
  instance: keyof typeof clientInstanceMap
}

export function getClient<BaseSegment extends string>(
  baseSegment = '' as NoTrailingSegment<'/', BaseSegment, true>,
  options?: Partial<ClientOptions>
) {
  const {
    type = 'api',
    version = 'v1',
    noVersioning = false,
    instance = 'clientInstance',
  } = options || {}

  const getRequestConfig = ({ url, ...restConfig }: AxiosRequestConfig) => {
    return {
      // www.example.com/api/v1/...
      url: noVersioning
        ? `${baseSegment}/${url}`
        : `${type}/${version}${baseSegment ? `/${baseSegment}` : ''}${url ? `/${url}` : ''}`,

      ...restConfig,
    }
  }

  const currentInstance = clientInstanceMap[instance]

  return {
    /**
     * To fetch a resource
     * @param url api path
     * @param paramConfig axios parameters
     */
    get: async <TRes = unknown>(url: string, paramConfig: ParamConfig = {}) =>
      currentInstance
        .request<unknown, AxiosResponse<TRes>>(
          getRequestConfig({ url, method: 'GET', ...paramConfig })
        )
        .then((response) => {
          return response
        })
        .catch((err) => {
          throw err
        }),

    /**
     * To create a resource
     * @param url api path
     * @param data Body to send
     * @param paramConfig axios parameters
     */
    post: async <TData = unknown, TRes = unknown>(
      url: string,
      data: TData,
      paramConfig: ParamConfig = {}
    ) =>
      currentInstance
        .request<TData, AxiosResponse<TRes>>(
          getRequestConfig({ url, method: 'POST', data, ...paramConfig })
        )
        .then((response) => {
          return response
        })
        .catch((err) => {
          throw err
        }),

    /**
     * To update a full data of resource
     * @param url api path
     * @param data Body to send
     * @param paramConfig axios parameters
     */
    put: async <TData = unknown, TRes = unknown>(
      url: string,
      data: TData,
      paramConfig: ParamConfig = {}
    ) =>
      currentInstance
        .request<TData, AxiosResponse<TRes>>(
          getRequestConfig({ url, method: 'PUT', data, ...paramConfig })
        )
        .then((response) => {
          return response
        })
        .catch((err) => {
          throw err
        }),

    /**
     * To update partial data of a resource
     * @param url api path
     * @param data Body to send
     * @param paramConfig axios parameters
     */
    patch: async <TData = unknown, TRes = unknown>(
      url: string,
      data: TData,
      paramConfig: ParamConfig = {}
    ) =>
      currentInstance
        .request<TData, AxiosResponse<TRes>>(
          getRequestConfig({ url, method: 'PATCH', data, ...paramConfig })
        )
        .then((response) => {
          return response
        })
        .catch((err) => {
          throw err
        }),

    /**
     * To delete the resource
     * @param url api path
     * @param paramConfig axios parameters
     */
    delete: async <TRes = unknown>(
      url: string,
      paramConfig: ParamConfig = {}
    ) =>
      currentInstance
        .request<unknown, AxiosResponse<TRes>>(
          getRequestConfig({ url, method: 'DELETE', ...paramConfig })
        )
        .then((response) => {
          return response
        })
        .catch((err) => {
          throw err
        }),
  }
}

export type IClient = ReturnType<typeof getClient>

export const dataClient = getClient()
